import { create } from 'zustand';
import type { OrderDetail } from '../lib/orders';
import {
  fetchAvailableRoutes,
  scheduleAppointment as apiScheduleAppointment,
  bumpCapacity as apiBumpCapacity,
  type RouteOption,
  type AppointmentType,
} from '../lib/appointments';

type SubmitResult = { ok: boolean; message?: string } | null;

export type OpslagRedenChoice = 'Goodwill' | 'Onze fout' | 'Coulance' | 'Anders' | '';
export const OPSLAG_REDEN_OPTIONS: ReadonlyArray<Exclude<OpslagRedenChoice, ''>> = [
  'Goodwill',
  'Onze fout',
  'Coulance',
  'Anders',
];

/**
 * Een "reschedule" is een wijziging van een afspraak die al gepland staat —
 * status is al "Ophalen ingepland" of "Terugbrengen ingepland". First-time
 * plannen (status "Ophalen plannen" → "Ophalen ingepland") is geen reschedule
 * en gebruikt de bestaande S03-bevestiging via status-change flow.
 */
function isRescheduleForType(order: OrderDetail, type: AppointmentType): boolean {
  if (type === 'pickup') return order.status === 'Ophalen ingepland';
  return order.status === 'Terugbrengen ingepland';
}

interface SchedulerState {
  isOpen: boolean;
  order: OrderDetail | null;
  primaryType: AppointmentType;

  // Primary picker — bevat in office-mode alle relevante routes
  // (incl. vandaag, vol, niet_voorstellen) met badges per route.
  routes: RouteOption[];
  selectedRouteId: string | null;
  notes: string;
  showExtraRoutes: boolean;
  isLoadingRoutes: boolean;
  routesError: string | null;

  // Reschedule flag — alleen relevant als order al status "Ophalen ingepland"
  // of "Terugbrengen ingepland" heeft. Bepaalt of de klant een bevestigingsmail krijgt.
  informerenKlant: boolean;

  // Also-deliver
  alsoDeliver: boolean;
  deliveryRoutes: RouteOption[];
  deliverySelectedRouteId: string | null;
  deliveryNotes: string;
  deliveryShowExtra: boolean;
  isLoadingDeliveryRoutes: boolean;
  deliveryRoutesError: string | null;

  // Capacity bump
  capacityBumpOpen: boolean;
  capacityBumpRouteId: string | null;
  capacityBumpType: AppointmentType;
  capacityBumpDelta: number;
  capacityBumpReason: string;
  isBumpingCapacity: boolean;
  capacityBumpError: string | null;

  // Bypass postcode-filter (route buiten dienstgebied — eenmalige uitzondering)
  bypassActive: boolean;
  bypassConfirmOpen: boolean;
  bypassReden: string;
  bypassError: string | null;
  /** Filter de bypass-routelijst op één master-route (bv. "Rotterdam"). null = alle. */
  bypassRouteNaamFilter: string | null;

  // Opslag-waiver (kantoor schakelt opslagkosten kwijtschelden voor deze WO)
  geenOpslagkosten: boolean;
  redenGeenOpslagkostenChoice: OpslagRedenChoice;
  redenGeenOpslagkostenOther: string;
  opslagWaiverError: string | null;

  // Submit lifecycle
  isSubmitting: boolean;
  submitResult: SubmitResult;

  onSubmitSuccess: (() => void) | null;

  // Actions
  openForPickup: (order: OrderDetail, onSuccess?: () => void) => void;
  openForDelivery: (order: OrderDetail, onSuccess?: () => void) => void;
  close: () => void;

  loadRoutes: () => Promise<void>;
  toggleShowExtra: () => void;
  selectRoute: (routeId: string | null) => void;
  setNotes: (notes: string) => void;
  setInformerenKlant: (v: boolean) => void;

  toggleAlsoDeliver: () => void;
  loadDeliveryRoutes: () => Promise<void>;
  toggleDeliveryShowExtra: () => void;
  selectDeliveryRoute: (routeId: string | null) => void;
  setDeliveryNotes: (notes: string) => void;

  openCapacityBump: (routeId: string, type: AppointmentType) => void;
  closeCapacityBump: () => void;
  setBumpDelta: (delta: number) => void;
  setBumpReason: (reason: string) => void;
  submitCapacityBump: () => Promise<void>;

  openBypassConfirm: () => void;
  closeBypassConfirm: () => void;
  setBypassReden: (reden: string) => void;
  confirmBypass: () => Promise<void>;
  setBypassRouteNaamFilter: (naam: string | null) => void;

  setGeenOpslagkosten: (v: boolean) => void;
  setRedenGeenOpslagkostenChoice: (v: OpslagRedenChoice) => void;
  setRedenGeenOpslagkostenOther: (v: string) => void;

  submit: () => Promise<void>;
}

const INITIAL_STATE = {
  isOpen: false,
  order: null,
  primaryType: 'pickup' as AppointmentType,

  routes: [],
  selectedRouteId: null,
  notes: '',
  showExtraRoutes: false,
  isLoadingRoutes: false,
  routesError: null,

  alsoDeliver: false,
  deliveryRoutes: [],
  deliverySelectedRouteId: null,
  deliveryNotes: '',
  deliveryShowExtra: false,
  isLoadingDeliveryRoutes: false,
  deliveryRoutesError: null,

  capacityBumpOpen: false,
  capacityBumpRouteId: null,
  capacityBumpType: 'pickup' as AppointmentType,
  capacityBumpDelta: 1,
  capacityBumpReason: '',
  isBumpingCapacity: false,
  capacityBumpError: null,

  bypassActive: false,
  bypassConfirmOpen: false,
  bypassReden: '',
  bypassError: null,
  bypassRouteNaamFilter: null,

  geenOpslagkosten: false,
  redenGeenOpslagkostenChoice: '' as OpslagRedenChoice,
  redenGeenOpslagkostenOther: '',
  opslagWaiverError: null,

  informerenKlant: true,

  isSubmitting: false,
  submitResult: null as SubmitResult,

  onSubmitSuccess: null,
};

export const useAppointmentSchedulerStore = create<SchedulerState>()((set, get) => ({
  ...INITIAL_STATE,

  openForPickup: (order, onSuccess) => {
    set({
      ...INITIAL_STATE,
      isOpen: true,
      order,
      primaryType: 'pickup',
      onSubmitSuccess: onSuccess ?? null,
    });
    get().loadRoutes();
  },

  openForDelivery: (order, onSuccess) => {
    set({
      ...INITIAL_STATE,
      isOpen: true,
      order,
      primaryType: 'delivery',
      onSubmitSuccess: onSuccess ?? null,
    });
    get().loadRoutes();
  },

  close: () => set({ ...INITIAL_STATE }),

  loadRoutes: async () => {
    const { order, primaryType, bypassActive } = get();
    if (!order) return;
    set({ isLoadingRoutes: true, routesError: null });
    try {
      const routes = await fetchAvailableRoutes(order.id, primaryType, {
        bypassPostcodeFilter: bypassActive,
      });
      set({ routes, isLoadingRoutes: false });
    } catch (err) {
      set({
        isLoadingRoutes: false,
        routesError: err instanceof Error ? err.message : 'Routes laden mislukt',
      });
    }
  },

  toggleShowExtra: () => set((s) => ({ showExtraRoutes: !s.showExtraRoutes })),

  selectRoute: (routeId) => set({ selectedRouteId: routeId }),

  setNotes: (notes) => set({ notes }),

  setInformerenKlant: (informerenKlant) => set({ informerenKlant }),

  toggleAlsoDeliver: () => {
    const next = !get().alsoDeliver;
    set({ alsoDeliver: next });
    if (next && get().deliveryRoutes.length === 0) {
      get().loadDeliveryRoutes();
    }
  },

  loadDeliveryRoutes: async () => {
    const { order, bypassActive } = get();
    if (!order) return;
    set({ isLoadingDeliveryRoutes: true, deliveryRoutesError: null });
    try {
      const deliveryRoutes = await fetchAvailableRoutes(order.id, 'delivery', {
        bypassPostcodeFilter: bypassActive,
      });
      set({ deliveryRoutes, isLoadingDeliveryRoutes: false });
    } catch (err) {
      set({
        isLoadingDeliveryRoutes: false,
        deliveryRoutesError: err instanceof Error ? err.message : 'Routes laden mislukt',
      });
    }
  },

  toggleDeliveryShowExtra: () => set((s) => ({ deliveryShowExtra: !s.deliveryShowExtra })),

  selectDeliveryRoute: (routeId) => set({ deliverySelectedRouteId: routeId }),

  setDeliveryNotes: (deliveryNotes) => set({ deliveryNotes }),

  openCapacityBump: (routeId, type) => set({
    capacityBumpOpen: true,
    capacityBumpRouteId: routeId,
    capacityBumpType: type,
    capacityBumpDelta: 1,
    capacityBumpReason: '',
    capacityBumpError: null,
  }),

  closeCapacityBump: () => set({
    capacityBumpOpen: false,
    capacityBumpRouteId: null,
    capacityBumpDelta: 1,
    capacityBumpReason: '',
    capacityBumpError: null,
  }),

  setBumpDelta: (capacityBumpDelta) => set({ capacityBumpDelta }),

  setBumpReason: (capacityBumpReason) => set({ capacityBumpReason }),

  submitCapacityBump: async () => {
    const {
      order,
      capacityBumpRouteId,
      capacityBumpType,
      capacityBumpDelta,
      capacityBumpReason,
    } = get();
    if (!order || !capacityBumpRouteId) return;
    if (capacityBumpReason.trim().length < 3) {
      set({ capacityBumpError: 'Geef een reden op (min. 3 tekens).' });
      return;
    }
    if (capacityBumpDelta < 1 || capacityBumpDelta > 10) {
      set({ capacityBumpError: 'Delta moet tussen 1 en 10 zijn.' });
      return;
    }

    set({ isBumpingCapacity: true, capacityBumpError: null });
    try {
      await apiBumpCapacity(
        order.id,
        capacityBumpRouteId,
        capacityBumpType,
        capacityBumpDelta,
        capacityBumpReason.trim()
      );
      set({
        isBumpingCapacity: false,
        capacityBumpOpen: false,
        capacityBumpRouteId: null,
        capacityBumpDelta: 1,
        capacityBumpReason: '',
      });
      // Herlaad routes om nieuwe capaciteit te tonen.
      if (capacityBumpType === get().primaryType) {
        await get().loadRoutes();
      } else {
        await get().loadDeliveryRoutes();
      }
    } catch (err) {
      set({
        isBumpingCapacity: false,
        capacityBumpError: err instanceof Error ? err.message : 'Capaciteit ophogen mislukt',
      });
    }
  },

  openBypassConfirm: () => set({
    bypassConfirmOpen: true,
    bypassError: null,
  }),

  closeBypassConfirm: () => set({
    bypassConfirmOpen: false,
    bypassReden: '',
    bypassError: null,
  }),

  setBypassReden: (bypassReden) => set({ bypassReden, bypassError: null }),

  setBypassRouteNaamFilter: (bypassRouteNaamFilter) => set({
    bypassRouteNaamFilter,
    // Reset selectie als die niet meer past binnen het filter
    selectedRouteId: null,
  }),

  setGeenOpslagkosten: (geenOpslagkosten) => set({
    geenOpslagkosten,
    // Bij uitzetten: reset reden + foutmelding zodat de waiver-card schoon staat.
    redenGeenOpslagkostenChoice: geenOpslagkosten ? get().redenGeenOpslagkostenChoice : '',
    redenGeenOpslagkostenOther: geenOpslagkosten ? get().redenGeenOpslagkostenOther : '',
    opslagWaiverError: null,
  }),

  setRedenGeenOpslagkostenChoice: (redenGeenOpslagkostenChoice) => set({
    redenGeenOpslagkostenChoice,
    opslagWaiverError: null,
  }),

  setRedenGeenOpslagkostenOther: (redenGeenOpslagkostenOther) => set({
    redenGeenOpslagkostenOther,
    opslagWaiverError: null,
  }),

  confirmBypass: async () => {
    const reden = get().bypassReden.trim();
    if (reden.length < 3) {
      set({ bypassError: 'Geef een reden op (min. 3 tekens).' });
      return;
    }
    set({
      bypassActive: true,
      bypassConfirmOpen: false,
      bypassError: null,
      // Reset huidige selectie — de oude routelijst gaat verdwijnen.
      selectedRouteId: null,
      deliverySelectedRouteId: null,
    });
    await get().loadRoutes();
    if (get().alsoDeliver) {
      await get().loadDeliveryRoutes();
    }
  },

  submit: async () => {
    const state = get();
    if (!state.order || !state.selectedRouteId) return;

    // Resolve opslag-waiver reden vóór de async submit. Alleen relevant bij
    // delivery + actieve waiver — opslag triggert nooit op pickup.
    let resolvedOpslagReden = '';
    if (state.primaryType === 'delivery' && state.geenOpslagkosten) {
      resolvedOpslagReden = state.redenGeenOpslagkostenChoice === 'Anders'
        ? state.redenGeenOpslagkostenOther.trim()
        : state.redenGeenOpslagkostenChoice;
      if (resolvedOpslagReden.length < 3) {
        set({
          opslagWaiverError: state.redenGeenOpslagkostenChoice === 'Anders'
            ? 'Geef een reden op (min. 3 tekens).'
            : 'Kies een reden.',
        });
        return;
      }
    }

    set({ isSubmitting: true, submitResult: null, opslagWaiverError: null });

    // Reschedule? Alleen dan is informerenKlant relevant (bij first-time triggert
     // de bestaande S03/S08 status-change flow).
    const isPrimaryReschedule = isRescheduleForType(state.order, state.primaryType);

    const baseOptions: {
      informerenKlant?: boolean;
      bypassPostcodeFilter?: boolean;
      bypassReden?: string;
      geenOpslagkosten?: boolean;
      redenGeenOpslagkosten?: string;
    } = {};
    if (isPrimaryReschedule) baseOptions.informerenKlant = state.informerenKlant;
    if (state.bypassActive) {
      baseOptions.bypassPostcodeFilter = true;
      baseOptions.bypassReden = state.bypassReden.trim();
    }
    // Alleen bij delivery + actieve waiver doorzetten. Pickup raakt opslag nooit
    // en een ongezette toggle betekent "geen wijziging" voor de Apex-service
    // (eerder gezette Geen_Opslagkosten__c blijft staan tot expliciet teruggedraaid).
    if (state.primaryType === 'delivery' && state.geenOpslagkosten) {
      baseOptions.geenOpslagkosten = true;
      baseOptions.redenGeenOpslagkosten = resolvedOpslagReden;
    }

    try {
      await apiScheduleAppointment(
        state.order.id,
        state.selectedRouteId,
        state.primaryType,
        state.notes.trim(),
        baseOptions
      );
    } catch (err) {
      set({
        isSubmitting: false,
        submitResult: {
          ok: false,
          message: err instanceof Error ? err.message : 'Plannen mislukt',
        },
      });
      return;
    }

    if (state.alsoDeliver && state.deliverySelectedRouteId) {
      const deliveryOptions: {
        bypassPostcodeFilter?: boolean;
        bypassReden?: string;
      } = {};
      if (state.bypassActive) {
        deliveryOptions.bypassPostcodeFilter = true;
        deliveryOptions.bypassReden = state.bypassReden.trim();
      }
      try {
        await apiScheduleAppointment(
          state.order.id,
          state.deliverySelectedRouteId,
          'delivery',
          state.deliveryNotes.trim(),
          deliveryOptions
        );
      } catch (err) {
        set({
          isSubmitting: false,
          submitResult: {
            ok: false,
            message:
              `${state.primaryType === 'pickup' ? 'Ophaal' : 'Aflever'} is ingepland, ` +
              `maar aflever-afspraak mislukte: ${err instanceof Error ? err.message : 'onbekende fout'}. ` +
              `Probeer aflever opnieuw.`,
          },
        });
        // Laat submitResult staan; roep callback toch aan zodat order refetched
        state.onSubmitSuccess?.();
        return;
      }
    }

    set({
      isSubmitting: false,
      submitResult: { ok: true, message: 'Afspraak ingepland' },
    });
    state.onSubmitSuccess?.();
  },
}));
